"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { MapPin, Phone, Mail, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactConsultation() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-consultation" ref={sectionRef} className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className={`grid md:grid-cols-2 gap-12 md:gap-16 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-display text-foreground mb-4">
                Get in Touch
              </h2>
              <p className="text-lg !text-[hsl(0,0%,100%)]">
                Contact us for a consultation on exclusive property management services.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-lg bg-primary/10 text-primary transition-all duration-[var(--duration-fast)] group-hover:bg-primary group-hover:text-foreground-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Address</h3>
                  <p className="!text-[hsl(0,0%,100%)]">8 The Green Suite #25244 Dover, DE 19901</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-lg bg-primary/10 text-primary transition-all duration-[var(--duration-fast)] group-hover:bg-primary group-hover:text-foreground-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                  <p className="!text-[hsl(0,0%,100%)]">(917)470-5987</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-lg bg-primary/10 text-primary transition-all duration-[var(--duration-fast)] group-hover:bg-primary group-hover:text-foreground-primary">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email</h3>
                  <p className="!text-[hsl(0,0%,100%)]">contact@omwmgmt.com</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-muted-light-background rounded-lg p-8 shadow-soft border border-muted">
            <FormProvider formId="contact-consultation-form" sectionName="contact-consultation" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              minLength: 10
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. We will contact you shortly.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary text-foreground-primary mb-4">
          <Mail className="size-8" />
        </div>
        <h3 className="text-xl font-display text-foreground mb-2">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must be less than 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-muted focus-visible:border-primary px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-muted focus-visible:border-primary px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Phone number must be at least 10 digits"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(917)470-5987" className="border-b-2 border-t-0 border-l-0 border-r-0 rounded-none border-muted focus-visible:border-primary px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must be less than 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your property management needs..." className="min-h-32 resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[var(--duration-medium)] hover:scale-[1.02]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}